import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import {
  List,
  TextField,
  EditButton,
  ShowButton,
  Datagrid,
  BooleanField,
  FunctionField,
  TextInput,
  ReferenceInput,
  SimpleList,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  ReferenceField,
  TopToolbar,
  CreateButton,
  AutocompleteInput,
  NullableBooleanInput,
  SelectInput,
} from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { administrationRequests } from "api/administrationApi";
import { DISCIPLINES, MOBILE_BREAKPOINT } from "utils/constants";
import { useMediaQuery } from "@mui/material";
import { EmptyList } from "server/components";
import { settings } from "app";
import { EducationsExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { MobileListRow } from "features/MobileListRow";

const disciplineFilters = [
  <TextInput
    label="Име на български език"
    source="nameBg"
    style={{ width: 400 }}
    alwaysOn
  />,
  <ReferenceInput
    source="departmentId"
    reference={administrationRequests.getDepartmentsSelect()}
    label="Катедра"
    style={{ width: 500 }}
    alwaysOn
  >
    <AutocompleteInput
      label="Катедра"
      optionText="fullName"
      style={{ width: 500 }}
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="structuralUnitId"
    reference={administrationRequests.getStructuralUnitsSelect()}
    filter={{ filterByUser: true }}
    label="Структурна единица"
    style={{ width: 375 }}
    alwaysOn
  >
    <SelectInput
      label="Структурна единица"
      emptyText="Всички"
      style={{ width: 375 }}
    />
  </ReferenceInput>,
  <NullableBooleanInput
    label="Има практически изпит"
    source="hasPracticalExam"
    alwaysOn
    nullLabel="Всички"
    style={{ width: 200 }}
  />,
];

const ListDisciplines = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const userPermissions = getPermissions();

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        {hasCreate && <CreateButton />}
        <ExportXlsButton exporter={EducationsExporter(DISCIPLINES)} />
      </TopToolbar>
    );
  };

  const categoryNames = {
    1: "Основна",
    2: "Спорт",
    3: "Чужд език",
  };

  return (
    <List
      title="Дисциплини"
      filters={disciplineFilters}
      actions={<ListActions hasCreate={checkPermission(userPermissions, permissions.canCreateDisciplines)} />}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow fields={[{ label: "Име на български език", value: record.nameBg }]} />
          )}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Име на английски език", value: record.nameEn }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Главна дисциплина",
                    value: (
                      <ReferenceField
                        source="parentId"
                        reference={administrationRequests.getDisciplinesSelect()}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Катедра",
                    value: (
                      <ReferenceArrayField
                        source="departmentIds"
                        reference={administrationRequests.getDepartmentsSelect()}
                        record={record}
                      >
                        <SingleFieldList linkType={false}>
                          <ChipField source="name" />
                        </SingleFieldList>
                      </ReferenceArrayField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Категория",
                    value: categoryNames[record.category] || "Неизвестна категория",
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Може да се запише условно",
                    value: record.isRequiredForAdvance ? "Да" : "Не",
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Има практически изпит",
                    value: record.hasPracticalExam ? "Да" : "Не",
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={() => (
            <>
              <SecuredComponent permission={permissions.canUpdateDisciplines}>
                <EditButton label="" />
              </SecuredComponent>
              <ShowButton label="" />
            </>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="nameBg"
            label="Име на български език"
            sortable={false}
          />
          <TextField
            source="nameEn"
            label="Име на английски език"
            sortable={false}
          />

          <ReferenceField
            label="Главна дисциплина"
            source="parentId"
            reference={administrationRequests.getDisciplinesSelect()}
          >
            <TextField source="name" />
          </ReferenceField>

          <ReferenceArrayField
            label="Катедра"
            source="departmentIds"
            reference={administrationRequests.getDepartmentsSelect()}
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>

          <FunctionField
            label="Категория"
            render={record => categoryNames[record.category] || "Неизвестна категория"}
          />
          <BooleanField
            source="isRequiredForAdvance"
            label="Може да се запише условно"
            sortable={false}
          />
          <BooleanField
            source="hasPracticalExam"
            label="Има практически изпит"
            sortable={false}
          />
          <SecuredComponent permission={permissions.canUpdateDisciplines}>
            <EditButton />
          </SecuredComponent>
          <ShowButton />
        </Datagrid>
      )}
    </List>
  );
};
export default ListDisciplines;
